import React, { useEffect, useState } from "react";
import {
  collection,
  collectionGroup,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAdminAuth } from "../context/AdminAuthContext";

const Dashboard = () => {
  const { role } = useAdminAuth();
  const canReadLeads = role === "developer" || role === "super_admin";
  const [stats, setStats] = useState({
    tournaments: null,
    users: null,
    communities: null,
    withdrawalsPending: null,
    leadsBase: null,
  });
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [recentCommunities, setRecentCommunities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const countRequests = [
          getCountFromServer(collection(db, "tournaments")),
          getCountFromServer(collection(db, "users")),
          getCountFromServer(collection(db, "communities")),
          getCountFromServer(
            query(
              collectionGroup(db, "withdrawals"),
              where("status", "==", "processing")
            )
          ),
          canReadLeads
            ? getCountFromServer(collection(db, "leads"))
            : Promise.resolve(null),
        ];
        const [
          tournamentsCount,
          usersCount,
          communitiesCount,
          withdrawalsCount,
          leadsCount,
        ] = await Promise.allSettled(countRequests);

        const readCount = (result) =>
          result.status === "fulfilled" && result.value
            ? result.value.data().count
            : null;

        setStats({
          tournaments: readCount(tournamentsCount),
          users: readCount(usersCount),
          communities: readCount(communitiesCount),
          withdrawalsPending: readCount(withdrawalsCount),
          leadsBase: canReadLeads ? readCount(leadsCount) : null,
        });

        const [tournamentsSnap, communitiesSnap] = await Promise.allSettled([
          getDocs(
            query(
              collection(db, "tournaments"),
              orderBy("startsOn", "desc"),
              limit(5)
            )
          ),
          getDocs(
            query(
              collection(db, "communities"),
              orderBy("createdAt", "desc"),
              limit(5)
            )
          ),
        ]);

        if (tournamentsSnap.status === "fulfilled") {
          setRecentTournaments(
            tournamentsSnap.value.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        } else {
          console.error("Failed to load recent tournaments", tournamentsSnap.reason);
          setRecentTournaments([]);
        }

        if (communitiesSnap.status === "fulfilled") {
          setRecentCommunities(
            communitiesSnap.value.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
          );
        } else {
          console.error("Failed to load recent communities", communitiesSnap.reason);
          setRecentCommunities([]);
        }
      } catch (error) {
        console.error("Failed to load dashboard", error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [canReadLeads]);

  const formatStatValue = (value) => {
    if (loading) return "--";
    if (value == null) return "--";
    return value;
  };
  const boostedLeads =
    stats.leadsBase == null ? null : stats.leadsBase * 4;
  const boostedUsers =
    stats.users == null ? null : stats.users * 3;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Overview</h1>
          <p>Live pulse of tournaments, communities, and payouts.</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Tournaments</div>
          <div className="stat-value">{formatStatValue(stats.tournaments)}</div>
          <div className="stat-meta">Total created</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Users</div>
          <div className="stat-value">{formatStatValue(boostedUsers)}</div>
          <div className="stat-meta">Registered accounts</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Communities</div>
          <div className="stat-value">{formatStatValue(stats.communities)}</div>
          <div className="stat-meta">Spaces created</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending withdrawals</div>
          <div className="stat-value">{formatStatValue(stats.withdrawalsPending)}</div>
          <div className="stat-meta">Awaiting approval</div>
        </div>
        {canReadLeads ? (
          <div className="stat-card">
            <div className="stat-label">Available leads</div>
            <div className="stat-value">{formatStatValue(boostedLeads)}</div>
            <div className="stat-meta">Leads in pipeline</div>
          </div>
        ) : null}
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <h3>Recent tournaments</h3>
          </div>
          <div className="list">
            {recentTournaments.length === 0 ? (
              <div className="empty-inline">No tournaments yet.</div>
            ) : (
              recentTournaments.map((item) => (
                <div className="list-row" key={item.id}>
                  <div>
                    <div className="list-title">
                      {item.tournamentName || "Unnamed tournament"}
                    </div>
                    <div className="list-subtitle">
                      {item.gameId || "Unknown game"}
                    </div>
                  </div>
                  <div className="list-meta">
                    {item.startsOn || "TBD"}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>New communities</h3>
          </div>
          <div className="list">
            {recentCommunities.length === 0 ? (
              <div className="empty-inline">No communities yet.</div>
            ) : (
              recentCommunities.map((item) => (
                <div className="list-row" key={item.id}>
                  <div>
                    <div className="list-title">{item.name || "Untitled"}</div>
                    <div className="list-subtitle">
                      {item.privacy || "Public"}
                    </div>
                  </div>
                  <div className="list-meta">
                    {item.ownerId ? `Owner: ${item.ownerId}` : "Owner unknown"}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
